import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { LivestockDiseaseEvent, LivestockHolding, LivestockMovement, OutbreakDashboard, Page, PestDiseaseOutbreak } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatCard, StatusBadge, TabStrip } from '../../ui/components';

const SPECIES = ['CATTLE', 'PIG', 'GOAT', 'SHEEP', 'POULTRY_LAYER', 'POULTRY_BROILER', 'DUCK', 'BEE_COLONY'] as const;
const NOTIFIABLE_ANIMAL_DISEASE_CODES = [
  'AFRICAN_SWINE_FEVER',
  'AVIAN_INFLUENZA_H5N1',
  'NEWCASTLE_DISEASE',
  'FOOT_AND_MOUTH_DISEASE',
  'BRUCELLOSIS',
  'RABIES',
  'ANTHRAX',
  'PORCINE_REPRODUCTIVE_RESPIRATORY_SYNDROME',
] as const;
const PLANT_PEST_AGENT_CODES = ['FALL_ARMYWORM', 'COCONUT_RHINOCEROS_BEETLE', 'CITRUS_GREENING_HLB', 'FRUIT_FLY_BACTROCERA', 'BLACK_SIGATOKA', 'COCOA_POD_BORER', 'RED_PALM_WEEVIL'] as const;

type Tab = 'holdings' | 'movements' | 'disease-events' | 'outbreaks';

/** M33 Agriculture — livestock holdings, inter-holding movement control, notifiable disease events, and declared outbreaks. */
export function Livestock() {
  const [tab, setTab] = useState<Tab>('holdings');
  return (
    <div>
      <PageHeader title="Livestock & Outbreaks" subtitle="Holdings, movement control, notifiable disease events, and pest/disease outbreaks (M33)." />
      <TabStrip
        tabs={[
          { key: 'holdings', label: 'Holdings' },
          { key: 'movements', label: 'Movements' },
          { key: 'disease-events', label: 'Disease Events' },
          { key: 'outbreaks', label: 'Outbreaks' },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === 'holdings' ? <HoldingsTab /> : tab === 'movements' ? <MovementsTab /> : tab === 'disease-events' ? <DiseaseEventsTab /> : <OutbreaksTab />}
    </div>
  );
}

// ── Holdings ─────────────────────────────────────────────────────────────

function HoldingsTab() {
  const { persona } = useAuth();
  const isAgriOfficer = persona ? persona.roles.includes('AGRI_OFFICER') : false;
  const canList = persona ? persona.roles.some((r) => ['AGRI_OFFICER', 'BIOSECURITY_OFFICER'].includes(r)) : false;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const path = useMemo(() => `/api/v1/m33/livestock-holdings?limit=50${status ? `&status=${status}` : ''}`, [status]);
  const listQ = useApiQuery<Page<LivestockHolding>>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {['ACTIVE', 'QUARANTINED', 'CLOSED'].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>
          + Register holding
        </button>
      </div>
      {!canList ? <RoleNotice roles={['AGRI_OFFICER', 'BIOSECURITY_OFFICER']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No livestock holdings match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Holding No</th>
                <th className="table-head">Farm</th>
                <th className="table-head">District</th>
                <th className="table-head">Species</th>
                <th className="table-head">Headcount</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((h) => (
                <tr key={h.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(h.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{h.holdingNo}</td>
                  <td className="table-cell">{h.farmName}</td>
                  <td className="table-cell text-xs text-slate-500">{h.district}</td>
                  <td className="table-cell text-xs">{h.species.map((s) => s.replaceAll('_', ' ')).join(', ')}</td>
                  <td className="table-cell text-xs">{h.headcountTotal}</td>
                  <td className="table-cell">
                    <StatusBadge status={h.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <HoldingDetailModal id={selectedId} isAgriOfficer={isAgriOfficer} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <NewHoldingModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function HoldingDetailModal({ id, isAgriOfficer, onClose, onChanged }: { id: string; isAgriOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const hq = useApiQuery<LivestockHolding>(`/api/v1/m33/livestock-holdings/${id}`);
  const h = hq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showClose, setShowClose] = useState(false);
  const [reason, setReason] = useState('');

  const close = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m33/livestock-holdings/${id}/close`, { reason });
      hq.reload();
      onChanged();
      setShowClose(false);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={h ? `${h.holdingNo} — ${h.farmName}` : 'Livestock holding'} onClose={onClose}>
      {hq.error ? <ErrorNote message={hq.error} /> : null}
      {hq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {h ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Status" value={<StatusBadge status={h.status} />} />
            <Detail label="Owner" value={`${h.ownerType}:${h.ownerId}`} mono />
            <Detail label="District" value={h.district} />
            <Detail label="Headcount" value={h.headcountTotal} />
            <Detail label="Species" value={h.species.map((s) => s.replaceAll('_', ' ')).join(', ')} />
          </div>
          {h.quarantineReason ? <ErrorNote message={`Quarantined: ${h.quarantineReason} (${formatDateTime(h.quarantinedAt)})`} /> : null}
          {h.closedReason ? <Detail label="Closed reason" value={h.closedReason} /> : null}

          {isAgriOfficer && h.status !== 'CLOSED' ? (
            <div className="border-t border-slate-100 pt-4">
              {!showClose ? (
                <button className="btn-danger" disabled={busy} onClick={() => setShowClose(true)}>
                  Close holding
                </button>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <Field label="Reason">
                    <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowClose(false)}>
                      Cancel
                    </button>
                    <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={close}>
                      {busy ? 'Closing…' : 'Confirm close'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewHoldingModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [ownerType, setOwnerType] = useState<'PERSON' | 'ENTITY'>('PERSON');
  const [ownerId, setOwnerId] = useState('');
  const [farmName, setFarmName] = useState('');
  const [district, setDistrict] = useState('Grand Anse Mahe');
  const [species, setSpecies] = useState<string[]>([]);
  const [headcountTotal, setHeadcountTotal] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (v: string) => setSpecies((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m33/livestock-holdings', {
        ownerType,
        ownerId,
        farmName,
        district,
        species,
        headcountTotal: headcountTotal ? Number.parseInt(headcountTotal, 10) : undefined,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register livestock holding" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Owner type">
            <select className="field-input" value={ownerType} onChange={(e) => setOwnerType(e.target.value as 'PERSON' | 'ENTITY')}>
              <option value="PERSON">Person</option>
              <option value="ENTITY">Entity</option>
            </select>
          </Field>
          <Field label={ownerType === 'PERSON' ? 'NIN' : 'Business ID'}>
            <input className="field-input" value={ownerId} onChange={(e) => setOwnerId(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Farm name">
            <input className="field-input" value={farmName} onChange={(e) => setFarmName(e.target.value)} />
          </Field>
          <Field label="District">
            <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
          </Field>
        </div>
        <div>
          <div className="field-label">Species</div>
          <div className="flex flex-wrap gap-2">
            {SPECIES.map((s) => (
              <label key={s} className={`cursor-pointer rounded-lg border px-2.5 py-1 text-xs font-medium ${species.includes(s) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-300 text-slate-600'}`}>
                <input type="checkbox" className="mr-1.5" checked={species.includes(s)} onChange={() => toggle(s)} />
                {s.replaceAll('_', ' ')}
              </label>
            ))}
          </div>
        </div>
        <Field label="Headcount (optional)">
          <input className="field-input" type="number" value={headcountTotal} onChange={(e) => setHeadcountTotal(e.target.value)} />
        </Field>
        <button className="btn-primary w-full" disabled={busy || !ownerId || !farmName || species.length === 0} onClick={submit}>
          {busy ? 'Registering…' : 'Register holding'}
        </button>
      </div>
    </Modal>
  );
}

// ── Movements ────────────────────────────────────────────────────────────

function HoldingPicker({ value, onPick, label }: { value: LivestockHolding | null; onPick: (h: LivestockHolding | null) => void; label: string }) {
  const [q, setQ] = useState('');
  const path = useMemo(() => (q.length >= 2 ? `/api/v1/m33/livestock-holdings?limit=8&q=${encodeURIComponent(q)}` : null), [q]);
  const results = useApiQuery<Page<LivestockHolding>>(path);
  if (value) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
        <span>
          <span className="font-mono font-medium">{value.holdingNo}</span> — {value.farmName}
        </span>
        <button className="text-xs font-semibold text-brand-700 underline" onClick={() => onPick(null)}>
          Change
        </button>
      </div>
    );
  }
  return (
    <div>
      <input className="field-input" placeholder={label} value={q} onChange={(e) => setQ(e.target.value)} />
      {results.data && results.data.items.length > 0 ? (
        <div className="mt-1 max-h-32 overflow-y-auto rounded-lg border border-slate-200">
          {results.data.items.map((h) => (
            <button key={h.id} className="block w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50" onClick={() => onPick(h)}>
              <span className="font-mono font-medium">{h.holdingNo}</span> — {h.farmName} ({h.status})
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function MovementsTab() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isAgriOfficer = roles.includes('AGRI_OFFICER');
  const isBioOfficer = roles.includes('BIOSECURITY_OFFICER');
  const canApprove = isAgriOfficer || isBioOfficer;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const path = useMemo(() => `/api/v1/m33/livestock-movements?limit=50${status ? `&status=${status}` : ''}`, [status]);
  const listQ = useApiQuery<Page<LivestockMovement>>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {['REQUESTED', 'APPROVED', 'REJECTED'].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>
          + Request movement
        </button>
      </div>
      {!canApprove ? <RoleNotice roles={['AGRI_OFFICER', 'BIOSECURITY_OFFICER']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No livestock movements match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Movement Ref</th>
                <th className="table-head">Destination</th>
                <th className="table-head">Species</th>
                <th className="table-head">Headcount</th>
                <th className="table-head">Date</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((m) => (
                <tr key={m.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(m.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{m.movementRef}</td>
                  <td className="table-cell text-xs">
                    {m.destinationKind}
                    {m.requiresBiosecurityOverride ? <div className="text-[10px] font-semibold text-amber-600">Outbreak-zone override required</div> : null}
                  </td>
                  <td className="table-cell text-xs">{m.species.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs">{m.headcount}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDate(m.movementDate)}</td>
                  <td className="table-cell">
                    <StatusBadge status={m.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <MovementDetailModal
          id={selectedId}
          isAgriOfficer={isAgriOfficer}
          isBioOfficer={isBioOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
      {showCreate ? (
        <NewMovementModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function MovementDetailModal({
  id,
  isAgriOfficer,
  isBioOfficer,
  onClose,
  onChanged,
}: {
  id: string;
  isAgriOfficer: boolean;
  isBioOfficer: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const mq = useApiQuery<LivestockMovement>(`/api/v1/m33/livestock-movements/${id}`);
  const m = mq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAction, setShowAction] = useState<'approve' | 'reject' | null>(null);
  const [note, setNote] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      mq.reload();
      onChanged();
      setShowAction(null);
      setNote('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const confirm = () =>
    call(() => {
      if (showAction === 'approve') return api.post(`/api/v1/m33/livestock-movements/${id}/approve`, { overrideReason: note || undefined });
      return api.post(`/api/v1/m33/livestock-movements/${id}/reject`, { reason: note });
    });

  const canApproveThis = m && m.status === 'REQUESTED' && (m.requiresBiosecurityOverride ? isBioOfficer : isAgriOfficer || isBioOfficer);

  return (
    <Modal title={m ? m.movementRef : 'Livestock movement'} onClose={onClose}>
      {mq.error ? <ErrorNote message={mq.error} /> : null}
      {mq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {m ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Status" value={<StatusBadge status={m.status} />} />
            <Detail label="Species" value={m.species.replaceAll('_', ' ')} />
            <Detail label="Headcount" value={m.headcount} />
            <Detail label="Movement date" value={formatDate(m.movementDate)} />
            <Detail label="Destination" value={m.destinationKind} />
            <Detail label="Description" value={m.toDescription ?? '—'} />
          </div>
          {m.requiresBiosecurityOverride ? (
            <ErrorNote message="Source holding is in an active outbreak's restricted district — requires BIOSECURITY_OFFICER approval with an override reason." />
          ) : null}
          {m.rejectionReason ? <ErrorNote message={`Rejected: ${m.rejectionReason}`} /> : null}

          {canApproveThis ? (
            <div className="border-t border-slate-100 pt-4">
              {!showAction ? (
                <div className="flex gap-2">
                  <button className="btn-success" disabled={busy} onClick={() => setShowAction('approve')}>
                    Approve
                  </button>
                  <button className="btn-danger" disabled={busy} onClick={() => setShowAction('reject')}>
                    Reject
                  </button>
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <Field label={showAction === 'approve' ? (m.requiresBiosecurityOverride ? 'Override reason (required)' : 'Note (optional)') : 'Rejection reason'}>
                    <textarea className="field-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowAction(null)}>
                      Cancel
                    </button>
                    <button
                      className={showAction === 'approve' ? 'btn-success' : 'btn-danger'}
                      disabled={busy || (showAction === 'approve' && m.requiresBiosecurityOverride && note.trim().length === 0)}
                      onClick={confirm}
                    >
                      {busy ? 'Submitting…' : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewMovementModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [fromHolding, setFromHolding] = useState<LivestockHolding | null>(null);
  const [destinationKind, setDestinationKind] = useState<'HOLDING' | 'ABATTOIR' | 'EXPORT' | 'OTHER'>('ABATTOIR');
  const [toDescription, setToDescription] = useState('');
  const [species, setSpecies] = useState<(typeof SPECIES)[number]>('PIG');
  const [headcount, setHeadcount] = useState('');
  const [movementDate, setMovementDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!fromHolding) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m33/livestock-movements', {
        fromHoldingId: fromHolding.id,
        destinationKind,
        toDescription: toDescription || undefined,
        species,
        headcount: Number.parseInt(headcount, 10),
        movementDate: new Date(movementDate).toISOString(),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Request livestock movement" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <Field label="Source holding">
          <HoldingPicker value={fromHolding} onPick={setFromHolding} label="Search source holding…" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Destination kind">
            <select className="field-input" value={destinationKind} onChange={(e) => setDestinationKind(e.target.value as typeof destinationKind)}>
              {(['HOLDING', 'ABATTOIR', 'EXPORT', 'OTHER'] as const).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Destination detail (optional)">
            <input className="field-input" value={toDescription} onChange={(e) => setToDescription(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Species">
            <select className="field-input" value={species} onChange={(e) => setSpecies(e.target.value as (typeof SPECIES)[number])}>
              {SPECIES.map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Headcount">
            <input className="field-input" type="number" value={headcount} onChange={(e) => setHeadcount(e.target.value)} />
          </Field>
          <Field label="Movement date">
            <input className="field-input" type="date" value={movementDate} onChange={(e) => setMovementDate(e.target.value)} />
          </Field>
        </div>
        <button className="btn-primary w-full" disabled={busy || !fromHolding || !headcount} onClick={submit}>
          {busy ? 'Submitting…' : 'Request movement'}
        </button>
      </div>
    </Modal>
  );
}

// ── Disease events ────────────────────────────────────────────────────────

function DiseaseEventsTab() {
  const { persona } = useAuth();
  const isVet = persona ? persona.roles.includes('VET') : false;
  const canCreate = persona ? persona.roles.some((r) => ['AGRI_OFFICER', 'VET'].includes(r)) : false;
  const canList = persona ? persona.roles.some((r) => ['AGRI_OFFICER', 'BIOSECURITY_OFFICER'].includes(r)) : false;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const path = useMemo(() => `/api/v1/m33/livestock-disease-events?limit=50${status ? `&status=${status}` : ''}`, [status]);
  const listQ = useApiQuery<Page<LivestockDiseaseEvent>>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {['REPORTED', 'INVESTIGATING', 'CONFIRMED', 'RULED_OUT', 'RESOLVED'].map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        {canCreate ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Report disease event
          </button>
        ) : null}
      </div>
      {!canList ? <RoleNotice roles={['AGRI_OFFICER', 'BIOSECURITY_OFFICER']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No disease events match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Event Ref</th>
                <th className="table-head">Disease</th>
                <th className="table-head">Holding</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((e) => (
                <tr key={e.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(e.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{e.eventRef}</td>
                  <td className="table-cell text-xs">{e.diseaseCode.replaceAll('_', ' ')}</td>
                  <td className="table-cell font-mono text-xs">{e.holdingId}</td>
                  <td className="table-cell">
                    <StatusBadge status={e.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <DiseaseEventDetailModal id={selectedId} isVet={isVet} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <NewDiseaseEventModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function DiseaseEventDetailModal({ id, isVet, onClose, onChanged }: { id: string; isVet: boolean; onClose: () => void; onChanged: () => void }) {
  const eq = useApiQuery<LivestockDiseaseEvent>(`/api/v1/m33/livestock-disease-events/${id}`);
  const e = eq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      eq.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? `${err.message} (${err.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const investigate = () => call(() => api.post(`/api/v1/m33/livestock-disease-events/${id}/investigate`));
  const confirmDisease = () => call(() => api.post(`/api/v1/m33/livestock-disease-events/${id}/confirm`, {}));
  const ruleOut = () => call(() => api.post(`/api/v1/m33/livestock-disease-events/${id}/rule-out`, {}));
  const resolve = () => call(() => api.post(`/api/v1/m33/livestock-disease-events/${id}/resolve`, {}));

  return (
    <Modal title={e ? `${e.eventRef} — ${e.diseaseCode.replaceAll('_', ' ')}` : 'Disease event'} onClose={onClose}>
      {eq.error ? <ErrorNote message={eq.error} /> : null}
      {eq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {e ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Status" value={<StatusBadge status={e.status} />} />
            <Detail label="Holding" value={e.holdingId} mono />
            <Detail label="Confirmed" value={formatDateTime(e.confirmedAt)} />
            <Detail label="Resolved" value={formatDateTime(e.resolvedAt)} />
          </div>
          {e.investigationNotes ? <Detail label="Investigation notes" value={e.investigationNotes} /> : null}

          {isVet ? (
            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
              {e.status === 'REPORTED' ? (
                <button className="btn-primary" disabled={busy} onClick={investigate}>
                  Start investigation
                </button>
              ) : null}
              {e.status === 'INVESTIGATING' ? (
                <>
                  <button className="btn-danger" disabled={busy} onClick={confirmDisease}>
                    Confirm (imposes quarantine)
                  </button>
                  <button className="btn-secondary" disabled={busy} onClick={ruleOut}>
                    Rule out
                  </button>
                </>
              ) : null}
              {e.status === 'CONFIRMED' ? (
                <button className="btn-success" disabled={busy} onClick={resolve}>
                  Resolve (lifts quarantine if no other active case)
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewDiseaseEventModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [holding, setHolding] = useState<LivestockHolding | null>(null);
  const [diseaseCode, setDiseaseCode] = useState<(typeof NOTIFIABLE_ANIMAL_DISEASE_CODES)[number]>('NEWCASTLE_DISEASE');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!holding) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m33/livestock-disease-events', { holdingId: holding.id, diseaseCode, notes: notes || undefined });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Report notifiable disease event" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <Field label="Holding">
          <HoldingPicker value={holding} onPick={setHolding} label="Search affected holding…" />
        </Field>
        <Field label="Notifiable disease">
          <select className="field-input" value={diseaseCode} onChange={(e) => setDiseaseCode(e.target.value as typeof diseaseCode)}>
            {NOTIFIABLE_ANIMAL_DISEASE_CODES.map((d) => (
              <option key={d} value={d}>
                {d.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Notes (optional)">
          <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <button className="btn-primary w-full" disabled={busy || !holding} onClick={submit}>
          {busy ? 'Reporting…' : 'Report event'}
        </button>
      </div>
    </Modal>
  );
}

// ── Outbreaks ─────────────────────────────────────────────────────────────

function OutbreaksTab() {
  const { persona } = useAuth();
  const isBioOfficer = persona ? persona.roles.includes('BIOSECURITY_OFFICER') : false;
  const canList = persona ? persona.roles.some((r) => ['BIOSECURITY_OFFICER', 'AGRI_OFFICER', 'VET'].includes(r)) : false;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const dashQ = useApiQuery<OutbreakDashboard>('/api/v1/m33/outbreaks/dashboard');
  const path = useMemo(() => `/api/v1/m33/outbreaks?limit=50${status ? `&status=${status}` : ''}`, [status]);
  const listQ = useApiQuery<Page<PestDiseaseOutbreak>>(path);

  return (
    <div>
      {!canList ? <RoleNotice roles={['BIOSECURITY_OFFICER', 'AGRI_OFFICER', 'VET']} /> : null}
      {dashQ.data ? (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Active outbreaks" value={dashQ.data.activeOutbreaks} tone={dashQ.data.activeOutbreaks > 0 ? 'bad' : 'good'} />
          <StatCard label="Contained" value={dashQ.data.containedOutbreaks} tone="warn" />
          <StatCard label="Restricted districts" value={dashQ.data.restrictedDistricts.length} hint={dashQ.data.restrictedDistricts.join(', ') || '—'} />
          <StatCard label="By kind" value={Object.entries(dashQ.data.byKind).map(([k, v]) => `${k}: ${v}`).join(', ') || '—'} />
        </div>
      ) : null}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {['ACTIVE', 'CONTAINED', 'CLOSED'].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {isBioOfficer ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Declare outbreak
          </button>
        ) : null}
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No outbreaks match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Kind</th>
                <th className="table-head">Agent</th>
                <th className="table-head">Districts</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((o) => (
                <tr key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(o.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{o.refNo}</td>
                  <td className="table-cell text-xs">{o.kind.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs">{o.agentCode.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">{o.districts.join(', ')}</td>
                  <td className="table-cell">
                    <StatusBadge status={o.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <OutbreakDetailModal
          id={selectedId}
          isBioOfficer={isBioOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            listQ.reload();
            dashQ.reload();
          }}
        />
      ) : null}
      {showCreate ? (
        <NewOutbreakModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
            dashQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function OutbreakDetailModal({ id, isBioOfficer, onClose, onChanged }: { id: string; isBioOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const oq = useApiQuery<PestDiseaseOutbreak>(`/api/v1/m33/outbreaks/${id}`);
  const o = oq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eventId, setEventId] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      oq.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const contain = () => call(() => api.post(`/api/v1/m33/outbreaks/${id}/contain`));
  const close = () => call(() => api.post(`/api/v1/m33/outbreaks/${id}/close`));
  const link = () => call(() => api.post(`/api/v1/m33/outbreaks/${id}/link-disease-event/${eventId}`)).then(() => setEventId(''));

  return (
    <Modal title={o ? `${o.refNo} — ${o.agentCode.replaceAll('_', ' ')}` : 'Outbreak'} onClose={onClose} wide>
      {oq.error ? <ErrorNote message={oq.error} /> : null}
      {oq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {o ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={o.status} />} />
            <Detail label="Kind" value={o.kind.replaceAll('_', ' ')} />
            <Detail label="Districts" value={o.districts.join(', ')} />
            <Detail label="Restricted districts" value={o.restrictedDistricts.join(', ') || '—'} />
            <Detail label="Movement control" value={o.movementControlActive ? 'Active' : 'Lifted'} />
            <Detail label="Declared" value={formatDateTime(o.declaredAt)} />
          </div>
          {o.description ? <Detail label="Description" value={o.description} /> : null}

          <div>
            <div className="field-label">Linked disease events ({o.diseaseEvents?.length ?? 0})</div>
            {(o.diseaseEvents ?? []).length === 0 ? (
              <p className="text-sm text-slate-400">None linked yet.</p>
            ) : (
              <ul className="text-sm">
                {(o.diseaseEvents ?? []).map((e) => (
                  <li key={e.id} className="flex items-center justify-between border-b border-slate-100 py-1">
                    <span className="font-mono text-xs">{e.eventRef}</span>
                    <StatusBadge status={e.status} />
                  </li>
                ))}
              </ul>
            )}
            {isBioOfficer ? (
              <div className="mt-2 flex gap-2">
                <input className="field-input" placeholder="Disease event ID to link" value={eventId} onChange={(e) => setEventId(e.target.value)} />
                <button className="btn-secondary" disabled={busy || !eventId} onClick={link}>
                  Link
                </button>
              </div>
            ) : null}
          </div>

          {isBioOfficer ? (
            <div className="flex gap-2 border-t border-slate-100 pt-4">
              {o.status === 'ACTIVE' ? (
                <button className="btn-secondary" disabled={busy} onClick={contain}>
                  Contain (lift movement control)
                </button>
              ) : null}
              {o.status !== 'CLOSED' ? (
                <button className="btn-danger" disabled={busy} onClick={close}>
                  Close outbreak
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewOutbreakModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [kind, setKind] = useState<'PLANT_PEST' | 'ANIMAL_DISEASE'>('ANIMAL_DISEASE');
  const [agentCode, setAgentCode] = useState<string>(NOTIFIABLE_ANIMAL_DISEASE_CODES[0]);
  const [districts, setDistricts] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const codes = kind === 'ANIMAL_DISEASE' ? NOTIFIABLE_ANIMAL_DISEASE_CODES : PLANT_PEST_AGENT_CODES;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m33/outbreaks', {
        kind,
        agentCode,
        districts: districts.split(',').map((d) => d.trim()).filter(Boolean),
        description: description || undefined,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Declare pest/disease outbreak" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Kind">
            <select
              className="field-input"
              value={kind}
              onChange={(e) => {
                const k = e.target.value as 'PLANT_PEST' | 'ANIMAL_DISEASE';
                setKind(k);
                setAgentCode(k === 'ANIMAL_DISEASE' ? NOTIFIABLE_ANIMAL_DISEASE_CODES[0] : PLANT_PEST_AGENT_CODES[0]);
              }}
            >
              <option value="ANIMAL_DISEASE">Animal disease</option>
              <option value="PLANT_PEST">Plant pest</option>
            </select>
          </Field>
          <Field label="Agent">
            <select className="field-input" value={agentCode} onChange={(e) => setAgentCode(e.target.value)}>
              {codes.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Districts (comma separated)">
          <input className="field-input" value={districts} onChange={(e) => setDistricts(e.target.value)} placeholder="Beau Vallon, Anse Royale" />
        </Field>
        <Field label="Description (optional)">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <button className="btn-primary w-full" disabled={busy || !districts.trim()} onClick={submit}>
          {busy ? 'Declaring…' : 'Declare outbreak (sets movement control)'}
        </button>
      </div>
    </Modal>
  );
}
